import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
	fetchUserById,
	fetchUserSetting,
	fetchAllUsers,
 } from '../slices/userSlice';
import UserDetails from '../components/users/UserDetails';
import { User, Edit3, Search, X } from 'react-feather';
import BncLogoSpinner from '../components/BncLogoSpinner';

export default function Users({ children }) {
	const dispatch = useDispatch();
	const [searchParams, setSearchParams] = useSearchParams();

	const currentUser = useSelector((state) => state.auth.self);
	const users = useSelector((state) => state.auth.users);
	const userById = useSelector((state) => state.auth.userById);
	const userSetting = useSelector((state) => state.auth.userSetting);

	const selectedUserId = searchParams.get('user');
	const [filterName, setFilterName] = useState('');
	const lastFetchedUserIdRef = useRef(null);

	useEffect(() => {
		// Avoid refetching on rerenders/remounts if we already have users.
		if (Array.isArray(users) && users.length > 0) return;
		dispatch(fetchAllUsers({ pageSize: 200, sortOrder: 'ASC' }));
	}, [dispatch, users]);

	useEffect(() => {
		if (!selectedUserId) return;
		// Prevent request storms if the component remounts or effects re-run.
		if (lastFetchedUserIdRef.current === selectedUserId) return;

		// If the store already holds the selected user's data, don't refetch.
		const hasUserById = userById?.id === selectedUserId;
		const hasUserSetting = userSetting?.id === selectedUserId;
		if (hasUserById && hasUserSetting) {
			lastFetchedUserIdRef.current = selectedUserId;
			return;
		}

		lastFetchedUserIdRef.current = selectedUserId;
		dispatch(fetchUserById(selectedUserId));
		dispatch(fetchUserSetting(selectedUserId));
	}, [dispatch, selectedUserId, userById, userSetting]);

	if (!currentUser) {
		return <BncLogoSpinner />;
	}

	const openDetails = (userId) => {
		if (!userId) return;
		const next = new URLSearchParams(searchParams);
		next.set('user', userId);
		setSearchParams(next);
	};

	const closeDetails = () => {
		const next = new URLSearchParams(searchParams);
		next.delete('user');
		lastFetchedUserIdRef.current = null;
		setSearchParams(next);
	};

	// Determine if we should filter the list locally since we are loading all users
	const filteredUsers = users?.filter((user) => {
		if (user?.email?.includes('_deactivate_')) return false;
		if (!filterName) return true;
		const query = filterName.toLowerCase();
		return (
			user?.firstName?.toLowerCase().includes(query) ||
			user?.lastName?.toLowerCase().includes(query) ||
			user?.email?.toLowerCase().includes(query)
		);
	});

	// Helper to get initials
	const getInitials = (firstName, lastName) => {
		return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
	};

	// Helper to get a consistent color based on name
	const getAvatarColor = (name) => {
		const colors = [
			'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 
			'bg-indigo-500', 'bg-purple-500', 'bg-pink-500', 'bg-teal-500'
		];
		let hash = 0;
		if (!name) return colors[0];
		for (let i = 0; i < name.length; i++) {
			hash = name.charCodeAt(i) + ((hash << 5) - hash);
		}
		return colors[Math.abs(hash) % colors.length];
	};

	return (
		<>
			<main className="flex-1 overflow-auto p-8 modern-dashboard">
				{children}
				<div className="dashboard-content pt-6">
				{/* Modern Search Bar */}
				<div className="flex justify-between items-center mb-6">
					<div className="relative w-full max-w-md">
						<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
							<Search size={18} className="text-gray-400" />
						</div>
						<input
							type="text"
							className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:border-blue-300 focus:shadow-outline-blue sm:text-sm transition duration-150 ease-in-out"
							placeholder="Search users..."
							value={filterName}
							onChange={(e) => setFilterName(e.target.value)}
						/>
						{filterName && (
							<button
								className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
								onClick={() => setFilterName('')}
							>
								<X size={16} />
							</button>
						)}
					</div>
				</div>

				{/* Modern Table */}
				<div className="flex flex-col">
					<div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
						<div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
							<div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
								<table className="min-w-full divide-y divide-gray-200">
									<thead className="bg-gray-50">
										<tr>
											<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												User
											</th>
											<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												Email
											</th>
											<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												Role
											</th>
											<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												Status
											</th>
											<th scope="col" className="relative px-6 py-3">
												<span className="sr-only">Edit</span>
											</th>
										</tr>
									</thead>
									<tbody className="bg-white divide-y divide-gray-200">
										{filteredUsers && filteredUsers.map((user) => (
											<tr key={user?.id} className="hover:bg-gray-50 transition-colors duration-150">
												<td className="px-6 py-4 whitespace-nowrap">
													<div className="flex items-center">
														<div className="flex-shrink-0 h-10 w-10">
															{user?.profilePicture ? (
																<img className="h-10 w-10 rounded-full object-cover" src={user.profilePicture} alt="" />
															) : (
																<div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${getAvatarColor(user?.firstName)}`}>
																	{getInitials(user?.firstName, user?.lastName)}
																</div>
															)}
														</div>
														<div className="ml-4">
															<div className="text-sm font-medium text-gray-900">
																{user?.firstName} {user?.lastName}
															</div>
														</div>
													</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<div className="text-sm text-gray-500">{user?.email}</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
														{user?.role || 'User'}
													</span>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user?.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
														{user?.isActive ? 'Active' : 'Inactive'}
													</span>
												</td>
												<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
													<button
														onClick={() => openDetails(user?.id)}
														className="text-indigo-600 hover:text-indigo-900 flex items-center justify-end gap-1 ml-auto"
													>
														Details <Edit3 size={16} />
													</button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>
			</main>

			{selectedUserId && (
				<UserDetails
					userId={selectedUserId}
					userDetails={userById}
					onClose={closeDetails}
				/>
			)}
		</>
	);
}
